import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';

//
// Telemetry fan-out.
//
// Stage Vault runs two platforms — Sentry for errors, PostHog for product
// analytics — and they overlap more than they differ: both identify the
// signed-in user, both track exceptions, both scope events to an environment.
// Reporting to one and forgetting the other is the failure mode this module
// exists to prevent, so every signal the apps raise goes through here and
// fans out to both.
//
// The one asymmetry left in code is deliberate: Sentry's Replay integration is
// configured in each app's main.js, while PostHog session recording is a
// project-level setting rather than a client one.
//

// PostHog property names are flat and dot-free by convention; Sentry tags use
// dots to namespace ("mux.error_code"). Translate rather than pick one style
// and make it read badly on the other platform.
function toPostHogKey(key) {
  return String(key).replace(/\./g, '_');
}

// Flatten tags and context into a single property bag for PostHog, which has
// no equivalent of Sentry's separate tag/context split.
function toPostHogProperties(tags, context) {
  const properties = {};
  for (const [key, value] of Object.entries(tags)) properties[toPostHogKey(key)] = value;
  for (const [key, value] of Object.entries(context)) properties[toPostHogKey(key)] = value;
  return properties;
}

// Attach the signed-in user to both platforms. Called wherever account data is
// refreshed, so re-identifying with the same guid is normal and cheap.
export function identifyUser(user) {
  if (!user?.guid) return;
  const name = `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim();

  try {
    // Without this, Sentry issues arrive anonymous — there's no way to tell
    // whether a crash hit one viewer or every viewer.
    Sentry.setUser({
      id: user.guid,
      email: user.email,
      username: name || undefined,
    });
  } catch (e) {
    console.error('Sentry identify failed:', e);
  }

  try {
    posthog.identify(user.guid, { email: user.email, name });
  } catch (e) {
    console.error('PostHog identify failed:', e);
  }
}

// Drop the user association on sign-out so the next session on a shared device
// doesn't inherit it.
export function resetUser() {
  try {
    Sentry.setUser(null);
  } catch (e) {
    console.error('Sentry reset failed:', e);
  }

  try {
    posthog.reset();
  } catch (e) {
    console.error('PostHog reset failed:', e);
  }
}

// Scope every event to the deploy it came from. Sentry takes this at init;
// PostHog has no environment concept, so register it as a super property that
// rides along on every event and can be filtered the same way.
export function registerEnvironment({ app, environment, version }) {
  try {
    posthog.register({ app, environment, app_version: version });
  } catch (e) {
    console.error('PostHog register failed:', e);
  }
}

// Report a handled error to both platforms.
//
// `tags` are the low-cardinality fields worth filtering and grouping on,
// `context` the detail you want once an issue is open, and `fingerprint`
// overrides Sentry's grouping (PostHog groups on the exception itself).
export function captureError(error, { tags = {}, contextName = 'context', context = {}, fingerprint } = {}) {
  try {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      for (const [key, value] of Object.entries(tags)) scope.setTag(key, value);
      scope.setContext(contextName, context);
      if (fingerprint) scope.setFingerprint(fingerprint);
      Sentry.captureException(error);
    });
  } catch (e) {
    console.error('Sentry capture failed:', e);
  }

  try {
    // PostHog's counterpart to captureException — feeds the same error
    // tracking surface that capture_exceptions fills automatically for
    // uncaught errors.
    posthog.captureException(error, toPostHogProperties(tags, context));
  } catch (e) {
    console.error('PostHog capture failed:', e);
  }
}
